import { Fragment, useState } from "react"
import { Chip, Collapse, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { useDialog } from "lib/@hooks/useDialog"
import { confirm } from "lib/@dialogs/confirm"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { GrantEditor } from "./grant-editor"
import { GrantDemandChip } from "./grant-demand-chip"
import { GrantBundleChip } from "./grant-bundle-chip"
import { BypassChip } from "./bypass-chip"
import { saveGrant, removeGrant } from "../controller/save-grant"
import { formatGrantDate, isGrantInactive, shortGrantState } from "./describe-grant-state"
import { listGrantBundles, grantConferredDemands } from "./describe-grant-bundles"

/**
 * One override as a table row, with the impact and audit detail behind an expander.
 *
 * Expired overrides stay listed (dimmed) rather than disappearing — a capability vanishing with
 * no trace of why is the confusion this feature exists to remove.
 */
export function GrantRow({ item: grant, onSaved }) {
    const { scope, subjectId, clientId } = useBoundContext()
    const editGrant = useDialog(<GrantEditor />, { maxWidth: "md", fullWidth: true })
    const [open, setOpen] = useState(false)
    const inactive = isGrantInactive(grant)
    const forced = Object.entries(grant.bypass ?? {}).filter(isSwitchedOn)
    // An override may confer hand-picked demands AND auth bundles at once, and a bundle's demands
    // live on the bundle — so the impact chips classify the union, or half of what the override
    // does would be invisible.
    const demands = grantConferredDemands(grant)
    const grantBundles = listGrantBundles(grant)

    return (
        <Fragment>
            <TableRow
                sx={{ opacity: inactive ? 0.6 : 1, "& > *": { borderBottom: "unset" } }}
                data-cy="access-grant-row"
            >
                <TableCell>
                    <IconButton
                        aria-label={open ? "Hide grant details" : "Show grant details"}
                        size="small"
                        onClick={toggle}
                        data-cy="access-grant-info"
                    >
                        <Iconify icon={open ? "mdi:chevron-down" : "mdi:chevron-right"} />
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="body2">{grant.name || "Untitled override"}</Typography>
                        <If value={grant.scope === "user"}>
                            <Chip size="small" variant="outlined" label="user" />
                        </If>
                        <Repeat list={forced} item={<BypassChip />} keyFn={keyOfBypass} />
                    </Stack>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{formatGrantDate(grant.startsAt) || "immediately"}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{formatGrantDate(grant.expiresAt) || "no expiry"}</Typography>
                </TableCell>
                <TableCell>
                    <Chip size="small" variant="outlined" label={shortGrantState(grant)} />
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
                        <Tooltip title={demands.join(", ")}>
                            <Typography variant="body2">{demandSummary()}</Typography>
                        </Tooltip>
                        <Repeat list={grantBundles} item={<GrantBundleChip />} keyFn={keyOfBundle} />
                    </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={grant.notes || ""} placement="top">
                        <Typography variant="body2" noWrap color={noteColour(grant.notes)} data-cy="access-grant-note">
                            {grant.notes || "No justification"}
                        </Typography>
                    </Tooltip>
                </TableCell>
                <TableCell align="right">
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={edit} data-cy="access-grant-edit">
                            <Iconify icon="mdi:pencil" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={remove} data-cy="access-grant-delete">
                            <Iconify icon="mdi:delete" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={8} sx={{ py: 0 }}>
                    <Collapse in={open} unmountOnExit>
                        <Stack spacing={1} py={1.5} pl={1}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    Impact &mdash; whether each demand actually reaches the user
                                </Typography>
                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                    <Repeat list={demands} item={<GrantDemandChip />} />
                                </Stack>
                            </Stack>
                            <If value={!!grant.notes}>
                                <Stack spacing={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        Justification
                                    </Typography>
                                    <Typography variant="body2">{grant.notes}</Typography>
                                </Stack>
                            </If>
                            <Typography variant="caption" color="text.secondary">
                                Granted by {grant.grantedBy || "unknown"}
                                {grant.grantedAt ? ` on ${formatGrantDate(grant.grantedAt)}` : ""}
                            </Typography>
                        </Stack>
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    )

    function toggle() {
        setOpen((current) => !current)
    }

    function keyOfBypass([stage]) {
        return stage
    }

    function keyOfBundle(bundle) {
        return bundle.id
    }

    function demandSummary() {
        if (demands.length === 1) return demands[0]
        return `${demands.length} demands`
    }

    function noteColour(notes) {
        return notes ? "text.primary" : "text.disabled"
    }

    /**
     * The row's OWN subject, never the tab's.
     *
     * A user's Access tab lists the client's overrides alongside that user's, so taking scope
     * and subject from the bound context rebinds a client-wide override to whoever it happened
     * to be opened from — silently revoking it for every other user of that client. The context
     * is only a fallback for a row that carries no subject of its own; the server refuses the
     * change either way.
     */
    function editableGrant() {
        return {
            ...grant,
            scope: grant.scope ?? scope,
            subjectId: grant.subjectId ?? subjectId,
            clientId: grant.clientId ?? clientId,
        }
    }

    async function edit() {
        const updated = await editGrant({ grant: editableGrant() })
        if (!updated) return
        try {
            await saveGrant(updated)
            successSnackbar("Override saved")
            onSaved?.()
        } catch (e) {
            errorSnackbar(e.message ?? "Failed to save the override")
        }
    }

    async function remove() {
        if (!(await confirm(`Delete the override "${grant.name}"?`, "Delete override"))) return
        try {
            await removeGrant(grant.id)
            successSnackbar("Override deleted")
            onSaved?.()
        } catch (e) {
            errorSnackbar(e.message ?? "Failed to delete the override")
        }
    }
}

function isSwitchedOn([, on]) {
    return on
}
